import React from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { Observe, property, freeze } from 'rewire-core';
import { Theme } from '@material-ui/core/styles';
import batchModeModel from '../batch/BatchModeModel';
import BatchPageView from '../batch/BatchPageView';
import BatchSummaryFooter from '../batch/BatchSummaryFooter';
import ErrorPage from '../ErrorPage';
import Report from '../report/Report';
import Analytics from '../report/Analytics';
import Routes from '../Routes';
import Schedule from '../schedule/Schedule';
import ScheduleFooter from '../schedule/ScheduleFooter';
import TimeOff from '../timeoff/TimeOff';
// import TimeOffFooter             from '../timeoff/TimeOffFooter';
import Settings from '../settings/Settings';
import SettingsFooter from '../settings/SettingsFooter';
import sideTogglePanelModel from '../side-navigation/SideTogglePanelModel';
import timecardModeModel from '../timecard/TimecardModeModel';
import TimecardPageView from '../timecard/TimecardPageView';
import TimecardFooter from '../timecard/TimecardFooter';
import TimecardSummaryFooter from '../timecard/TimecardSummaryFooter';
import TimecardTestPageView from '../test/TimecardTestPageView';
import TimecardTestFooter from '../test/TimecardTestFooter';
import Area from './Area';
import Home from './Home';
import Inbox from './Inbox';
import NoAccess from './NoAccess';
import MainContentNavbar from './MainContentNavbar';
import MainContentFooter from './MainContentFooter';
import {
  RouteManagerBase,
  jobScheduleRouteManager,
  employeeScheduleRouteManager,
  timeOffListRouteManager,
  timeOffCalendarRouteManager,
  payRouteManager,
  timecardSummaryRouteManager,
  timecardTestRouteManager,
  batchRouteManager,
  adjustmentsBatchRouteManager,
  batchSummaryRouteManager,
  reportRouteManager,
  analyticsRouteManager,
} from './RouteManager';
import Search from './Search';
import breadcrumbsModel from './BreadcrumbsModel';
import navigationTabsModel from './NavigationTabsModel';
import { history } from '../RouterWithHistory';
import { makeStyles } from '../utility/styles';
import SettingsSearch from '../settings/SettingsSearch';
import ScheduleSearch from './ScheduleSearch';
import searchBarComponenent from './SearchBarModel';

const useMainContentStyles = makeStyles((theme: Theme) => ({
  root: {
    width: 'calc(100% - 275px)',
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    padding: '0px',
    transition: theme.transitions.create(['width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),
  },
  sidebarIsClosed: {
    width: 'calc(100% - 44px)',
    transition: theme.transitions.create(['width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

function matches(path: string, route: string, exact = false) {
  return matchPath(path, { path: route, exact: exact });
}

// Observable Content for <MainContent />
const mainContent = property<any>(null);
const footerContent = property<any>(null);

function makeGetRouteMapFn() {
  const routeMap = {
    [Routes.JobSchedule]: {
      main: <Schedule />,
      search: ScheduleSearch,
      footer: <ScheduleFooter />,
      routeManager: jobScheduleRouteManager,
      exact: true,
    },

    [Routes.EmployeeSchedule]: {
      main: <Schedule />,
      search: ScheduleSearch,
      footer: <ScheduleFooter />,
      routeManager: employeeScheduleRouteManager,
      exact: true,
    },

    [Routes.TimeOffCalendar]: {
      routeManager: timeOffCalendarRouteManager,
      main: <TimeOff />,
      // footer: <TimeOffFooter />,
      exact: true,
    },

    [Routes.TimeOffList]: {
      routeManager: timeOffListRouteManager,
      main: <TimeOff />,
      // footer: <TimeOffFooter />,
      exact: true,
    },

    [Routes.Timecard]: {
      routeManager: payRouteManager,
      search: ScheduleSearch,
      main: <TimecardPageView />,
      footer: <TimecardFooter />,
      exact: true,
    },

    [Routes.TimecardSummary]: {
      routeManager: timecardSummaryRouteManager,
      search: ScheduleSearch,
      main: <TimecardPageView />,
      footer: <TimecardSummaryFooter />,
      exact: true,
    },

    [Routes.TimecardTest]: {
      routeManager: timecardTestRouteManager,
      main: <TimecardTestPageView />,
      footer: <TimecardTestFooter />,
      exact: true,
    },

    [Routes.Batch]: {
      routeManager: batchRouteManager,
      main: <BatchPageView />,
      exact: true,
    },

    [Routes.AdjustmentsBatch]: {
      routeManager: adjustmentsBatchRouteManager,
      main: <BatchPageView />,
      exact: true,
    },

    [Routes.BatchSummary]: {
      routeManager: batchSummaryRouteManager,
      main: <BatchPageView />,
      footer: <BatchSummaryFooter />,
      exact: true,
    },

    [Routes.Home]: {
      main: <Home />,
      exact: true,
    },

    [Routes.Inbox]: {
      main: <Inbox />,
      footer: <ScheduleFooter />,
      exact: true,
    },

    [Routes.Search]: {
      main: null,
      exact: true,
    },

    [Routes.Report]: {
      main: <Report />,
      routeManager: reportRouteManager,
      exact: true,
    },

    [Routes.Analytics]: {
      main: <Analytics />,
      routeManager: analyticsRouteManager,
      exact: true,
    },

    [Routes.Settings]: {
      main: null,
      search: SettingsSearch,
      footer: <SettingsFooter />,
      exact: false,
    },

    [Routes.Areas]: {
      main: <Area />,
      exact: false,
    },
  };

  return () => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    routeMap[Routes.Search].main = (
      <Search history={history} location={history.location} match={matches(history.location.pathname, Routes.Search, true)!} />
    );
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    routeMap[Routes.Settings].main = (
      <Settings history={history} location={history.location} match={matches(history.location.pathname, Routes.Settings, false)!} />
    );
    return routeMap;
  };
}

const getRouteMap = makeGetRouteMapFn();

const ObservableRouter = () => {
  let _mainContent: any = null;
  let _footerContent: any = null;
  let routeManager: any = null;
  let Search: any = null;

  // useLocation watches for location changes!
  const location = useLocation();
  const routeMap = getRouteMap();
  for (const [key, route] of Object.entries(routeMap)) {
    if (matches(location.pathname, key, route.exact)) {
      if (route.routeManager) routeManager = route.routeManager;
      _mainContent = !routeManager || routeManager.access ? route.main : <NoAccess />;
      if (route.footer) {
        _footerContent = !routeManager || routeManager?.access ? route.footer : null;
      }
      Search = route.search;
      break;
    }
  }

  if (!_mainContent) _mainContent = <ErrorPage />;

  // queue up the route changes on next animation frame
  requestAnimationFrame(() => {
    const routeWithinSamePage = _mainContent === mainContent();
    searchBarComponenent.SearchComponent = Search;
    if (!routeWithinSamePage) {
      freeze(() => {
        mainContent(_mainContent);
        footerContent(_footerContent);
      });
    }

    if (routeManager) {
      RouteManagerBase.current = routeManager;
      routeManager?.update();
      if (!routeWithinSamePage) {
        if (routeManager === payRouteManager) {
          timecardModeModel.mode = 'Timecard';
        } else if (routeManager === timecardSummaryRouteManager) {
          timecardModeModel.mode = 'Summary';
        } else if (routeManager === batchRouteManager) {
          batchModeModel.mode = 'Create';
        } else if (routeManager === adjustmentsBatchRouteManager) {
          batchModeModel.mode = 'Adjustments';
        } else if (routeManager === batchSummaryRouteManager) {
          batchModeModel.mode = 'Summary';
        }
      }
    } else {
      RouteManagerBase.updateRoute();
    }

    breadcrumbsModel.update();
    navigationTabsModel.update();
  });

  // always render null preventing any component redraws when this component changes
  return null;
};

const useInternalStyles = makeStyles((_theme: Theme) => ({
  mainContentBody: {
    width: '100%',
    padding: '8px 8px 4px 8px',
    overflowY: 'auto',
    display: 'flex',
    flex: '1',
  },
}));

// uses the ObservableRouter above to listen to route changes and Observe will update the sections when the route changes.
const MainContentInternal = React.memo(() => {
  const classes = useInternalStyles();
  return (
    <>
      <ObservableRouter />
      <MainContentNavbar />
      <div className={classes.mainContentBody}>
        <Observe>{mainContent}</Observe>
      </div>
      <MainContentFooter>
        <Observe>{footerContent}</Observe>
      </MainContentFooter>
    </>
  );
});

export default () => {
  const classes = useMainContentStyles();

  React.useEffect(() => {
    // returned function will be called on component unmount
    return () => {
      setTimeout(() => RouteManagerBase.updateRoute(), 0);
    };
  }, []);

  return (
    <Observe
      render={() => (
        <div id="main-content" className={classNames(classes.root, !sideTogglePanelModel.isOpen && classes.sidebarIsClosed)}>
          <MainContentInternal />
        </div>
      )}
    />
  );
};
